// Looking up (owner 2026-10-27: "not a lookup mode, but included in the normal controls of moving around"; "a bit
// smoother angle control like in lookup mode past a certain angle so looking feels more premium"): the tilt goes on
// past the map's lowest tilt, after a short nudge (PUSH), with no mode and no bar. The map pose under it stays a legal orbit pose (target
// on the ground, inside every limit); on top of it the camera turns up where it stands, by `head` radians, up to TOP
// above the horizon. A pan, a zoom, a lift and a turn keep working while looking up (the view stays up): left and right
// still circle round a place as on the map (owner 2026-10-27: "it just rotates around its own axis"), the head only
// takes the tilt; tilting back down lowers the head first, then the map tilts as before. Unlike the old sky view (skyview.js, kept but off), the
// turned pose stays on the camera between frames, so a grab or a wheel aims at what is on screen.
// In: the grab orbit's `over` hook (camerabounds.js: a right-drag, two fingers, Shift + Arrow Down pushed on at the
// lowest tilt). A drag applies at once (the grab orbit blends its tilt into it, camerabounds.js BLEND); a key step
// eases (TAU); a flight, a tour, a ride view or walking in lowers it (BACK).
// A saved view keeps it: `lookUp` (degrees of head) beside the map pose (main.js capture); opening the view puts it back
// (put: at once, or along the flight there).
import * as THREE from 'three/webgpu'; // maths only

const TAU = 0.07, BACK = 0.25; // s: the view eases to where the input put it / back down when the map takes the camera
const TOP = (80 * Math.PI) / 180; // the highest the view may look (rad above the horizon)
const RATE = 1; // a fed tilt as it comes (the grab orbit scales a drag's by LOOK_RATE)
const LOOK_RATE = 0.5, PUSH = (18 * Math.PI) / 180; // the look-up's tilt rate (of the map orbit's); the push past the lowest tilt (rad of drag)

const X = new THREE.Vector3(1, 0, 0), q = new THREE.Quaternion();
/** The camera, aimed at its target (lookAt), turned up `rad` about its own right axis: the head (also a saved view's picture, main.js still). */
export function turnUp(camera, rad) { if (rad > 0) camera.quaternion.multiply(q.setFromAxisAngle(X, rad)); }

export function createHeadLook({ camera, controls, nav = () => false }) {
  const s = { head: 0, goal: 0, held: false };
  const d = new THREE.Vector3();
  const baseTilt = () => { d.copy(controls.target).sub(camera.position); return Math.asin(THREE.MathUtils.clamp(d.y / Math.max(d.length(), 1e-6), -1, 1)); }; // rad, < 0 = down
  const api = {
    over: null,
    /** Looking up, or on the way back down. */
    active: () => s.goal > 0 || s.head > 1e-4,
    /** The tilt (rad; + = the camera rises = the view looks down): a drag's at once, as the map's tilt it goes on from
     *  (owner 2026-10-27: an eased one "twitches into a different mode"); eased = a key's step. The turn stays the orbit's. */
    feed(pitch, eased = false) {
      if (s.held || nav()) return false;
      s.goal = THREE.MathUtils.clamp(s.goal - pitch * RATE, 0, Math.max(0, TOP - baseTilt()));
      if (!eased) { s.head = s.goal; api.step(0); }
      return true;
    },
    /** A saved view's look-up (its `lookUp`, rad): the head there at once, as far as TOP lets it (main.js setView, and
     *  each step of a flight to the view). Not while held or while the map has the camera (a tour). */
    put(rad) {
      if (s.held || nav()) return false;
      s.goal = s.head = THREE.MathUtils.clamp(rad || 0, 0, Math.max(0, TOP - baseTilt()));
      camera.lookAt(controls.target); turnUp(camera, s.head); camera.updateMatrixWorld();
      return true;
    },
    /** Fine-tune and Film: no looking up while held (holding drops it at once). */
    hold(on) { s.held = !!on; if (s.held) { s.goal = s.head = 0; camera.lookAt(controls.target); } },
    busy: () => api.active() && Math.abs(s.goal - s.head) > 1e-4,
    state: () => ({ head: s.head, goal: s.goal, tilt: baseTilt() + s.head }), // QA (rad)
    /** Per frame, after the controls and the bounds: eases the turn and puts the head on the camera. */
    step(dt) {
      if (!api.active()) return;
      const back = nav();
      if (back) s.goal = 0;
      const k = 1 - Math.exp(-dt / (back ? BACK : TAU));
      s.head += (s.goal - s.head) * k;
      if (Math.abs(s.goal - s.head) < 1e-4) s.head = s.goal;
      camera.lookAt(controls.target);
      turnUp(camera, s.head); // about the camera's own right: up
      camera.updateMatrixWorld();
    },
  };
  // the grab orbit's hook. The nudge (owner 2026-10-27: "not go to that mode super easily, the same sort of nudge as
  // before"): at the lowest tilt a mostly vertical push of PUSH (the sky view's 18 degrees of drag) starts it, in the same
  // gesture (seamless). `tilt`: it takes the tilt only, the turn stays the map's orbit; `rate`: its tilt rate, of the
  // map's, which the map's tilt eases into over the last BLEND (camerabounds.js; "the angle is changing super freely")
  api.over = { active: api.active, feed: (y, p) => api.feed(p), held: () => s.held, seamless: true, strict: true, tilt: true, push: PUSH * LOOK_RATE, rate: LOOK_RATE };
  return api;
}
